/* tslint:disable:import-spacing no-inferrable-types variable-name align no-trailing-whitespace */
import { DecisionPoint } from './decision.point';
import { TSOLink }       from './t.s.o.link';
import { TSOSpeed }      from './t.s.o.speed';
import { Track }         from './track';
import { TrainClass }    from "./train.class";
import { InterLink }     from "./inter.link";

/**
 *
 * @class TSO
 *
 * @brief base model for a train slow order. It's really an aggregate of three subsections
 * of the 'slow_orders' section in the scenario:
 *
 *   - slow_orders
 *   - tso_links
 *   - tso_speeds
 *
 * those three subsections together, define the all the TSOs in bulk.  That is, any given
 * TSO is spread over those three subsections :(
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      July 2020
 *
 */
export class TSO {

  /** @var number the unique id of this tso (tso_id) */
  public             _id: number         = -1;

  /** @var string user label for tso (name) */
  public            _name: string        = '';

  /** @var string user description for tso (description) */
  public     _description: string        = '';

  /** @var number the start time (seconds from start of sim) for the tso(start) */
  public            _start: number       = 0;

  /** @var number how long it lasts in seconds (duration) */
  public       _duration: number         = 0;

  /** @var number factor (tonnage_release) */
  public        _tonnage: number         = 0;

  /** @var number side mileage bracket (east_mp) */
  public        _eastMile: number        = 0;
  public          _eastDP: DecisionPoint|null = null;

  /** @var number side mileage bracket (west_mp) */
  public        _westMile: number        = 0;
  public          _westDP: DecisionPoint|null = null;

  /** @var number zero based index for this item */
  public             _nth: number        = -1;

  /** @var number current speed for the FRT bucket */
  public        _frtSpeed: number = -1;

  /** @var number the current speed for the PSG bucket */
  public        _psgSpeed: number = -1;

  /** @var Map<number, TSOLink> the map of links in scope for this TSO */
  public            links: Map<number, TSOLink> = new Map<number, TSOLink>();

  /** @var Map<string, Map<number, TSOSpeed>> the map of speeds for this TSO */
  public           speeds: Map<string, Map<number, TSOSpeed>> = new Map<string, Map<number, TSOSpeed>>();

  /** @brief standard constructor */
  constructor(      id: number,
                  name: string,
           description: string,
                 start: number,
              duration: number,
               tonnage: number,
              eastMile: number,
                eastDP: DecisionPoint|null,
              westMile: number,
                westDP: DecisionPoint|null,
                   nth: number) {

    this._id          = id;
    this._name        = name;
    this._description = description;
    this._start       = start;
    this._duration    = duration;
    this._tonnage     = tonnage;
    this._eastMile    = eastMile;
    this._eastDP      = eastDP;
    this._westMile    = westMile;
    this._westDP      = westDP;
    this._nth         = nth;
  }

  /** helper to register a speed class */
  public addSpeedClass(bucket: string, tcId: number, ts: TSOSpeed) {

    /* figure out which supported speed bucket we are using ... */

    bucket = bucket.trim().toUpperCase();

    /* in our initial implementation, we only allow one speed per bucket */

    if ((bucket !== 'FRT') && (bucket !== 'PSG')) {

      /* if the bucket isn't one we support right now, just use freight */

      bucket = 'FRT';
    }

    if (!this.speeds.has(bucket)) {

      /* create an empty bucket for this speed class */

      const sc: Map<number, TSOSpeed> = new Map<number, TSOSpeed>();

      this.speeds.set(bucket, sc);
    }

    /* do we have a freight speed yet? */

    if ((this._frtSpeed < 0) && (bucket === 'FRT')) {

      this._frtSpeed = ts._speed;
    }

    /* do we have a passenger speed yet? */

    if ((this._psgSpeed < 0) && (bucket === 'PSG')) {

      this._psgSpeed = ts._speed;
    }

    /* save this speed class */

    const b: Map<number, TSOSpeed>|undefined = this.speeds.get(bucket);

    if (b) {

      b.set(tcId, ts);
    }
  }

  /** helper to reset the train classes for the given speed bucket */
  public setClasses(bucket: string, classes: TrainClass[]): void {

    let speed:  number = this._psgSpeed;

    if(bucket !== 'PSG') {

      bucket = 'FRT';
      speed  = this._frtSpeed;
    }

    const newSpeeds: Map<number, TSOSpeed> = new Map<number, TSOSpeed>();

    for (const tc of classes) {

      newSpeeds.set(tc._id, new TSOSpeed(
        this._id,
        tc._id,
        speed,
        tc,
        -1
      ));
    }

    this.speeds.set(bucket, newSpeeds);
  }

  /** helper to recalculate the links for this section of the network */
  public setLinks(links: Track[]): void {

    this.links = new Map<number, TSOLink>();

    let min: number = -1;
    let max: number = -1;

    for (const link of links) {

      /*
       * internal links are bounded by 0/0 mileage, but the ones on the ends have to have
       * their "outside" offset set relative to the nearest low (east side) decision point
       *
       */

      let  em: number = 0;
      let  wm: number = 0;

      /* is this an internal link? */

      if (min < 0) {

        /* first time through, figure out the outer interlinks (track bundles) */

        let bundles: (InterLink|null)[] = links.map(x => x._peers);
        min = Math.max(...bundles.map(x => x ? x._index : 0));
        max = Math.min(...bundles.map(x => x ? x._index : 0));
      }

      /* if there is only one bundle, then its touching both sides */

      if (min === max) {

        if (this._eastDP !== null) {

          if ((link._after !== null) && (link._after._id === this._eastDP._id)) {

            em = this._eastMile - this._eastDP._mileage;

          }
        }

        if (this._westDP !== null) {

          if ((link._after !== null) && (link._after._id === this._westDP._id)) {

            wm = this._westMile - this._westDP._mileage;

          }
        }

      } else {

        /* we may be at the min bundle, internal bundle or max bundle... */

        /* does it touch the low side? */

        if ((link._after !== null) && (this._eastDP && link._after._id === this._eastDP._id)) {

          em = this._eastMile - this._eastDP._mileage;

          /* the distance between this link and the next one, i.e. offset to end of link */

          wm = link.miles();

          if ((link._before !== null) && (link._before._id === this._eastDP._id)) {

            em = this._eastMile - this._eastDP._mileage;

            /* the distance between this link and the next one, i.e. offset to end of link */

            wm = link.miles();
          }

        } else if ((link._after !== null) && (this._westDP && link._after._id === this._westDP._id)) {

          wm = this._westMile - this._westDP._mileage;

          /* start of the link 0 miles */

          em = 0;

          if ((link._before !== null) && (link._before._id === this._westDP._id)) {

            wm = this._westMile - this._westDP._mileage;

            /* start of the link 0 miles */

            em = 0;
          }

        } /* end of touching one side or the other */

      } /* end of not being completely internal */

      this.links.set(link._id, new TSOLink(
        this._id,
        link._id,
        em,
        wm,
        link,
        -1
      ));

    } /* end of track walk */

  }

}
